/**
 * Centralised score colour utilities for the 1–5 evaluation scale.
 *
 * All components that colour-code scores should import from here so that
 * thresholds and colours are never duplicated or allowed to diverge.
 *
 * Bands (aligned with rubric anchor semantics):
 *   ≥ 4.5  very dark green  Excellent
 *   ≥ 4.0  dark green       Strong
 *   ≥ 3.0  light green      Moderate
 *   ≥ 2.0  yellow           Limited
 *   < 2.0  red              Poor
 */

/** Returns the hex colour for a 1–5 score. */
export const getScoreHex = (score: number): string => {
  if (score >= 4.5) return "#1b5e20";
  if (score >= 4.0) return "#2e7d32";
  if (score >= 3.0) return "#66bb6a";
  if (score >= 2.0) return "#ffc107";
  return "#f44336";
};

/** Returns the foreground text colour that gives adequate contrast on the score background. */
export const getScoreTextColor = (_score: number): string => "rgba(0,0,0,0.87)";

/**
 * Returns `sx` props for a MUI Chip whose background reflects the score.
 * Pass `score: undefined` to get a neutral disabled style.
 */
export const getScoreChipSx = (
  score: number | undefined,
): { backgroundColor: string; color: string } => {
  if (score === undefined)
    return { backgroundColor: "rgba(255,255,255,0.1)", color: "rgba(255,255,255,0.4)" };
  return {
    backgroundColor: getScoreHex(score),
    color: getScoreTextColor(score),
  };
};

/**
 * Returns a low-opacity rgba background suitable for table/matrix cells.
 * Pass `score: undefined` to get transparent.
 */
export const getScoreCellBg = (score: number | undefined): string => {
  if (score === undefined) return "transparent";
  if (score >= 4.5) return "rgba(27, 94, 32, 0.18)";
  if (score >= 4.0) return "rgba(46, 125, 50, 0.18)";
  if (score >= 3.0) return "rgba(102, 187, 106, 0.18)";
  if (score >= 2.0) return "rgba(255, 193, 7, 0.18)";
  return "rgba(244, 67, 54, 0.18)";
};

/** Returns the human-readable label for a 1–5 score. */
export const getScoreLabel = (score: number): string => {
  if (score >= 4.5) return "Excellent";
  if (score >= 4.0) return "Strong";
  if (score >= 3.0) return "Moderate";
  if (score >= 2.0) return "Limited";
  return "Poor";
};
